import { Filter } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../../../stores/authStore";
import { useToastStore } from "../../../stores/toastStore";
import { DataTable, cycleSortDir, type Column } from "../../components/DataTable";
import { MAX_PAGE_SIZE } from "../../../utils/formatters";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { DiscardConfirmDialog, useDiscardGuard } from "../../components/DiscardConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { AccessActionButtons } from "./components/AccessActionButtons";
import { ReviewBadge, StatusPill, TextCell } from "./components/StatusBadges";
import { PENDING_REVIEW_STATUS } from "./constants/pagConstants";
import {
  formatInventoryDate,
  hasOpenReviewRequest,
  isDateOverdue,
  pagErrorMessage,
} from "./helpers/pagHelpers";
import { usePagScopeFilter, useSelectedAccountNames } from "./hooks/pagHooks";
import {
  fetchPagReviewList,
  submitPagReviewDecision,
  type PagReviewRequestRow,
} from "./services/pagApi";

const RiskLevelBadge = ({ level }: { level: string | null | undefined }) => {
  if (!level) return <span className="text-[11px] text-muted-foreground">-</span>;
  const upper = level.toUpperCase();
  const toneMap: Record<string, "red" | "orange" | "amber" | "emerald" | "slate"> = {
    CRITICAL: "red",
    HIGH: "orange",
    MEDIUM: "amber",
    LOW: "emerald",
    UNCATEGORISED: "slate",
    UNCATEGORIZED: "slate",
  };
  const tone = toneMap[upper] ?? "slate";
  return <StatusPill label={level} tone={tone} />;
};

const PamStatusBadge = ({ status }: { status: string | null | undefined }) => {
  if (!status) return <span className="text-[11px] text-muted-foreground">-</span>;
  const upper = status.toUpperCase().replace(/\s+/g, "_");
  const tone = upper === "VAULTED" ? "emerald" : "red";
  return <StatusPill label={status} tone={tone} />;
};

type QueueAction = "approve" | "reject";

/** Only a request still awaiting a decision can be approved or rejected -
 *  the same rows the account tables close their own actions to. */
const isActionable = (row: PagReviewRequestRow) => hasOpenReviewRequest(row.status);

/** A review date in the past means the request has run over. Scoped to a
 *  pending review rather than every open request: a revoke carries the date
 *  of the review that prompted it, which is not a deadline of its own. */
const isOverdue = (row: PagReviewRequestRow) => {
  const date = row.targetDate ?? row.reviewDate;
  if (!date || !hasOpenReviewRequest(row.status)) return false;
  return isDateOverdue(date);
};

const ACTION_COPY: Record<QueueAction, { title: string; verb: string; tone: string }> = {
  approve: {
    title: "Approve requests",
    verb: "Approve",
    tone: "bg-indigo-600 hover:bg-indigo-500",
  },
  reject: {
    title: "Reject requests",
    verb: "Reject",
    tone: "bg-red-600 hover:bg-red-500",
  },
};

export const AccessGovernance = () => {
  const scope = usePagScopeFilter();
  const [rows, setRows] = useState<PagReviewRequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setAppliedSearch("");
      setCurrentPageIndex(0);
    }
  };

  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === appliedSearch) return;
    setAppliedSearch(next);
    setCurrentPageIndex(0);
  };
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(MAX_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<string | null>("reviewDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [queueAction, setQueueAction] = useState<QueueAction | null>(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [deciding, setDeciding] = useState(false);

  /* Paging, search and sort belong to the server, so every change re-reads. */
  const loadRequests = useCallback(async () => {
    const ids = scope.sourceSystemIds;
    if (!ids.length) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await fetchPagReviewList(
        {
          sourceSystemIds: ids,
          search: appliedSearch,
          sortBy: sortKey ?? "reviewDate",
          sortDir,
        },
        currentPageIndex,
        itemsPerPage,
      );
      setRows(result?.content ?? []);
      setTotal(result?.totalElements ?? 0);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not load review requests",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      setLoading(false);
    }
  }, [scope.sourceSystemIds, scope.reloadToken, currentPageIndex, itemsPerPage, appliedSearch, sortKey, sortDir]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const actionableRows = useMemo(() => rows.filter(isActionable), [rows]);
  const allSelected =
    actionableRows.length > 0 && selectedIds.length === actionableRows.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleRow = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.length === actionableRows.length ? [] : actionableRows.map((r) => r.reviewId),
    );

  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
    setCurrentPageIndex(0);
  };

  /* The endpoint decides by account name, so the selected rows are mapped
     back to theirs. */
  const selectedAccountNames = useSelectedAccountNames(rows, selectedIds);

  const confirmQueueAction = async () => {
    const action = queueAction;
    if (!action || selectedAccountNames.length === 0) return;
    try {
      const sysId =
        scope.sourceSystemIds[0] ?? (rows[0] as any)?.sourceSystemId ?? 0;
      if (!sysId) {
        useToastStore
          .getState()
          .showError("Decision failed", "No valid source system selected.");
        return;
      }
      setDeciding(true);
      const result = await submitPagReviewDecision({
        sourceSystemId: sysId,
        accountNames: selectedAccountNames,
        action: action === "approve" ? "APPROVE" : "REJECT",
        comment: decisionComment.trim(),
        decidedBy: useAuthStore.getState().user?.username ?? "",
      });
      setQueueAction(null);
      setDecisionComment("");
      setSelectedIds([]);
      await loadRequests();
      useToastStore
        .getState()
        .showSuccess(
          result?.message ?? "Decision recorded",
          `${result?.results?.length ?? selectedAccountNames.length} account(s) ${result?.actionPlan ? result.actionPlan.toLowerCase() : action + "d"
          }.`,
        );
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          action === "approve" ? "Could not approve the requests" : "Could not reject the requests",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      setDeciding(false);
    }
  };

  const columns = useMemo<Column<PagReviewRequestRow>[]>(
    () => [
      {
        key: "select",
        header: (
          <div className="flex items-center justify-center p-0.5">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={() => toggleAll()}
              disabled={actionableRows.length === 0}
              aria-label="Select all pending review requests"
            />
          </div>
        ),
        align: "center",
        headerAlign: "center",
        width: "64px",
        minWidth: 64,
        headerClassName: "!px-2",
        cellClassName: "!px-2 !overflow-visible",
        tooltip: false,
        render: (row) => (
          <div className="flex items-center justify-center p-0.5">
            <Checkbox
              checked={selectedIds.includes(row.reviewId)}
              onCheckedChange={() => toggleRow(row.reviewId)}
              disabled={!isActionable(row)}
              aria-label={`Select ${row.requestId}`}
            />
          </div>
        ),
      },
      {
        key: "requestId",
        header: "Request ID",
        align: "left",
        headerAlign: "center",
        width: "160px",
        minWidth: 160,
        sortable: true,
        render: (row) => (
          <span className="whitespace-nowrap break-normal text-sm text-foreground">
            {row.requestId}
          </span>
        ),
      },
      {
        key: "accountName",
        header: "Account",
        align: "left",
        headerAlign: "center",
        width: "150px",
        minWidth: 150,
        sortable: true,
        render: (row) => (
          <span className="whitespace-nowrap break-normal text-sm text-foreground">
            {row.accountName}
          </span>
        ),
      },
      {
        key: "displayName",
        header: "Display Name",
        align: "left",
        headerAlign: "center",
        width: "150px",
        minWidth: 150,
        sortable: true,
        render: (row) => <TextCell value={row.displayName} />,
      },
      {
        key: "sourceSystemName",
        header: "Source System",
        align: "left",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => <TextCell value={row.sourceSystemName} />,
      },
      {
        key: "groupNames",
        header: "Groups",
        align: "left",
        headerAlign: "center",
        width: "160px",
        minWidth: 160,
        render: (row) => (
          <TextCell value={row.groupNames && row.groupNames.length > 0 ? row.groupNames.join(", ") : null} wrap />
        ),
      },
      {
        key: "riskLevel",
        header: "Risk Level",
        align: "center",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => <RiskLevelBadge level={row.riskLevel} />,
      },
      {
        key: "pamStatus",
        header: "PAM Status",
        align: "center",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => <PamStatusBadge status={row.pamStatus} />,
      },
      {
        key: "justification",
        header: "Justification",
        align: "left",
        headerAlign: "center",
        width: "220px",
        minWidth: 220,
        sortable: true,
        ellipsis: 2,
        tooltip: (row) => row.justification ?? undefined,
        render: (row) => <TextCell value={row.justification} wrap />,
      },
      {
        key: "requestedBy",
        header: "Requested By",
        align: "left",
        headerAlign: "center",
        width: "150px",
        minWidth: 150,
        sortable: true,
        render: (row) => <TextCell value={row.requestedBy} />,
      },
      {
        key: "requestedOn",
        header: "Requested On",
        align: "left",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => (
          <span className="whitespace-nowrap break-normal text-sm text-foreground">
            {formatInventoryDate(row.requestedOn ?? null) ?? "-"}
          </span>
        ),
      },
      {
        key: "targetDate",
        header: "Target Date",
        align: "left",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => (
          <span className="whitespace-nowrap break-normal text-sm text-foreground">
            {formatInventoryDate(row.targetDate ?? null) ?? "-"}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        align: "center",
        headerAlign: "center",
        width: "160px",
        minWidth: 160,
        sortable: true,
        render: (row) => <ReviewBadge review={row.status} />,
      },
      {
        key: "reviewDate",
        header: "Review Date",
        align: "left",
        headerAlign: "center",
        width: "140px",
        minWidth: 140,
        sortable: true,
        render: (row) => (
          <span
            className={`whitespace-nowrap break-normal text-sm ${isOverdue(row) ? "font-semibold text-red-500" : "text-foreground"
              }`}
          >
            {formatInventoryDate(row.reviewDate) ?? "-"}
          </span>
        ),
      },
    ],
    [allSelected, someSelected, selectedIds, actionableRows],
  );

  const copy = queueAction ? ACTION_COPY[queueAction] : null;

  const handleCloseDecisionDialog = () => {
    setQueueAction(null);
    setDecisionComment("");
  };
  const isDecisionDirty = Boolean(decisionComment.trim());
  const { requestClose: requestCloseDecision, confirmProps: decisionConfirmProps } =
    useDiscardGuard(isDecisionDirty, handleCloseDecisionDialog);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full min-w-[180px] flex-col gap-1.5 sm:w-[200px]">
            <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
              Source System
            </span>
            <MultiSelectDropdown
              variant="form"
              selectAll
              options={scope.systemOptions}
              selected={scope.selectedSystems}
              onChange={scope.changeSystems}
              placeholder="Select source"
              selectedNoun="Systems"
            />
          </div>
          <div className="flex w-full min-w-[200px] flex-col gap-1.5 sm:w-[240px]">
            <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
              Resources
            </span>
            <MultiSelectDropdown
              variant="form"
              searchable
              selectAll
              options={scope.resourceOptions}
              selected={scope.resources}
              onChange={scope.setResources}
              placeholder="Select resource"
              selectedNoun="Resources"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => {
  
                setCurrentPageIndex(0);
                scope.apply();
              }}
              disabled={loading || scope.selectedSystems.length === 0 || scope.resources.length === 0}
              variant="indigo"
              className="justify-center min-w-[92px] gap-1.5 px-5 font-semibold disabled:opacity-60"
            >
              <Filter className="size-3.5" />
              Filter
            </Button>
            <Button
              type="button"
              onClick={scope.clear}
              disabled={loading}
              variant="outline"
              className="justify-center min-w-[92px] px-5 hover:border-indigo-400/60 hover:bg-muted/40 disabled:opacity-60"
            >
              Clear
            </Button>
          </div>
        </div>

        <TableToolbar
          /* flex-1 root: without this it stretches to fill the card. */
          className="max-h-fit"
          search={{
            value: search,
            onChange: handleSearchChange,
            onSubmit: submitSearch,



            placeholder: "Search",
            maxLength: 80,
            ariaLabel: "Search review requests",
          }}
          pageSize={itemsPerPage}
          pageSizeLabel="Rows per page"
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPageIndex(0);
          }}
          actions={
            <AccessActionButtons
              selectedCount={selectedIds.length}
              onApprove={() => setQueueAction("approve")}
              onDeny={() => setQueueAction("reject")}
              approveLabel="Approve"
              denyLabel="Reject"
            />
          }
        />

        <div
          className={`transition-opacity ${loading && rows.length > 0 ? "pointer-events-none opacity-60" : ""}`}
        >
          <DataTable
            columns={columns}
            data={rows}
            keyField="reviewId"
            loading={loading && rows.length === 0}
            page={currentPageIndex}
            pageSize={itemsPerPage}
            totalItems={total}
            onPageChange={setCurrentPageIndex}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            maxHeight="550px"
            rowClassName={(row) => (isOverdue(row) ? "bg-red-500/5" : "")}
            itemLabel="records"
            emptyMessage="No Data Available"
          />
        </div>
      </div>

      <Dialog
        open={queueAction !== null}
        onOpenChange={(next) => {
          if (!next && !deciding) {
            requestCloseDecision();
          }
        }}
      >
        <DialogContent
          className="gap-0 overflow-hidden p-0 sm:max-w-sm"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="gap-1 border-b border-border p-5 pr-12 text-left">
            <DialogTitle className="text-base font-semibold">{copy?.title}</DialogTitle>
            <DialogDescription className="sr-only">
              Confirm the decision for the selected review requests.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-5">
            <p className="text-sm text-foreground">
              {copy?.verb} {selectedIds.length}{" "}
              {selectedIds.length === 1 ? "selected request" : "selected requests"}?
            </p>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="review-decision-comment" className="text-sm font-medium text-foreground">
                Comment
              </label>
              <Textarea
                id="review-decision-comment"
                rows={3}
                maxLength={500}
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                placeholder="What did you verify before this decision?"
                className="resize-none bg-background"
              />
            </div>
          </div>

          <DialogFooter className="flex-row justify-end gap-3 border-t border-border p-5">
            <Button variant="outline"
              type="button"
              onClick={requestCloseDecision}
              disabled={deciding}
              className="justify-start rounded-md px-5 text-sm font-medium text-foreground hover:border-indigo-400/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button variant="ghost"
              type="button"
              onClick={() => void confirmQueueAction()}
              disabled={deciding}
              className={`justify-start h-auto px-0 py-0 font-normal inline-flex h-9 items-center rounded-md px-5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${copy?.tone ?? ""}`}
            >
              {deciding ? "Submitting" : copy?.verb}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DiscardConfirmDialog {...decisionConfirmProps} />
    </div>
  );
};
